/**
 * WS4 Data Tables content — typed transcript of the legacy vanilla
 * `#dataTablesModal` registry. Five tabs ship: Overview, Operating
 * Modes, Roles & Responsibilities, Handoff Protocols, and Performance
 * Metrics.
 *
 * Ported verbatim from:
 *   - `_legacy/ws4-assetlocker.js` lines 109-294 — the single source of
 *     truth for cell-by-cell content. Every label, pill, glyph, and
 *     coloured cell originates there.
 *
 * The map is keyed by workshop id so later workshops can supply their
 * own data without breaking the WS4-only consumer. Today only WS4 is
 * populated; consumers should treat a missing key as "no data yet".
 */

/* ----------------------------- Accents ---------------------------- */

/**
 * Accent slot — matches `dt-pill-*` / coloured-text usage in the legacy
 * markup. Folds back to the available `.dt-pill-*` variants in
 * `src/index.css` (orange / blue / green / cyan / melon / purple / red).
 * `melon` and `red` still share the `--accent-melon` swatch (legacy
 * `_legacy/xaes.css` line 1389 aliased them together); `purple` was
 * broken out to the real `--accent-purple` token in Phase 5b. Semantic
 * names are preserved per cell so future theme work can split melon
 * and red the same way.
 */
export type DtAccent =
  | "blue"
  | "cyan"
  | "green"
  | "gold"
  | "melon"
  | "orange"
  | "purple"
  | "red"
  | "sky"
  | "true-blue";

/** Allocation glyph accent — narrower than `DtAccent`. */
export type AllocationAccent = "green" | "gold" | "muted";

/* ------------------------------ KPI ------------------------------ */

export type KpiCard = {
  label: string;
  value: string;
  sub: string;
  accent: DtAccent;
};

/* --------------------------- Overview ---------------------------- */

export type CollaborationModel = {
  model: string;
  modelAccent: DtAccent;
  humanRole: string;
  agentRole: string;
  authority: string;
  authorityAccent: DtAccent;
  useCases: string;
  maturity: string;
  maturityAccent: DtAccent;
};

export type AdoptionBar = {
  label: string;
  pct: number;
  accent: DtAccent;
};

/* ------------------------- Operating Modes ----------------------- */

export type OperatingMode = {
  mode: string;
  modeAccent: DtAccent;
  trigger: string;
  latency: string;
  touchPoints: string;
  fallback: string;
  risk: string;
  riskAccent: DtAccent;
};

export type ModeTransition = {
  from: string;
  /** Omit to render `from` as plain text (legacy "Any" row). */
  fromAccent?: DtAccent;
  to: string;
  toAccent: DtAccent;
  condition: string;
  approver: string;
  cooling: string;
};

/* ----------------------------- Roles ----------------------------- */

export type RoleDefinition = {
  role: string;
  responsibilities: string;
  interaction: string;
  authority: string;
  skills: string;
};

export type RoleAllocation = {
  role: string;
  in: string;
  inAccent: AllocationAccent;
  above: string;
  aboveAccent: AllocationAccent;
  on: string;
  onAccent: AllocationAccent;
  out: string;
  outAccent: AllocationAccent;
};

/* ---------------------------- Handoff ---------------------------- */

export type HandoffTrigger = {
  category: string;
  condition: string;
  severity: string;
  severityAccent: DtAccent;
  channel: string;
  sla: string;
  auto: string;
};

export type HandoffStandard = {
  element: string;
  required: string;
  format: string;
  validation: string;
};

/* --------------------------- Performance ------------------------- */

export type PerfBenchmark = {
  metric: string;
  inLoop: string;
  inAccent?: DtAccent;
  aboveLoop: string;
  aboveAccent?: DtAccent;
  onLoop: string;
  onAccent?: DtAccent;
  outLoop: string;
  outAccent?: DtAccent;
  target: string;
};

export type SignalThreshold = {
  signal: string;
  green: string;
  amber: string;
  red: string;
  frequency: string;
};

/* ------------------------- Workshop bundle ----------------------- */

export type WorkshopDataTables = {
  /** Headline summary string — surfaced in dialog description. */
  summary: string;
  overviewKpis: KpiCard[];
  collaborationModels: CollaborationModel[];
  adoptionByBu: AdoptionBar[];
  operatingModes: OperatingMode[];
  modeTransitions: ModeTransition[];
  roleDefinitions: RoleDefinition[];
  roleAllocations: RoleAllocation[];
  handoffTriggers: HandoffTrigger[];
  handoffStandards: HandoffStandard[];
  performanceKpis: KpiCard[];
  performanceBenchmarks: PerfBenchmark[];
  signalThresholds: SignalThreshold[];
};

/* ------------------------------ Data ----------------------------- */

const WS4_DATA: WorkshopDataTables = {
  summary:
    "4 collaboration models · 3.2× decision speed · 68% oversight rate · 94% escalation success",

  /* ----- Overview KPIs (legacy lines 139-142) ----- */
  overviewKpis: [
    {
      label: "Collaboration Models",
      value: "4",
      sub: "Defined operating patterns",
      accent: "sky",
    },
    {
      label: "Avg. Decision Speed",
      value: "3.2×",
      sub: "Faster vs. human-only baseline",
      accent: "green",
    },
    {
      label: "Human Oversight Rate",
      value: "68%",
      sub: "Of all agentic actions reviewed",
      accent: "gold",
    },
    {
      label: "Escalation Success",
      value: "94%",
      sub: "Correctly routed to human",
      accent: "true-blue",
    },
  ],

  /* ----- Collaboration Model Summary (legacy lines 148-151) ----- */
  collaborationModels: [
    {
      model: "HUMAN IN-THE-LOOP",
      modelAccent: "orange",
      humanRole: "Reviews & approves every agent action before execution",
      agentRole: "Surfaces options, drafts outputs, flags anomalies",
      authority: "Human",
      authorityAccent: "gold",
      useCases:
        "High-stakes decisions, regulated processes, novel scenarios",
      maturity: "PRODUCTION",
      maturityAccent: "green",
    },
    {
      model: "HUMANS ABOVE-THE-LOOP",
      modelAccent: "blue",
      humanRole: "Sets goals, monitors KPIs, intervenes on exceptions",
      agentRole: "Executes autonomously within defined guardrails",
      authority: "Agent (bounded)",
      authorityAccent: "true-blue",
      useCases:
        "Repeatable workflows, data processing, routine reporting",
      maturity: "PRODUCTION",
      maturityAccent: "green",
    },
    {
      model: "HUMAN ON-THE-LOOP",
      modelAccent: "cyan",
      humanRole: "Monitors outputs asynchronously, can pause or redirect",
      agentRole: "Operates end-to-end, logs all actions for audit",
      authority: "Agent (audited)",
      authorityAccent: "sky",
      useCases:
        "Long-running research, content pipelines, data enrichment",
      maturity: "SCALING",
      maturityAccent: "orange",
    },
    {
      model: "HUMAN OUT-OF-LOOP",
      modelAccent: "purple",
      humanRole:
        "Designs system, defines acceptance criteria, reviews post-hoc",
      agentRole:
        "Fully autonomous; triggers, decides, executes, reports",
      authority: "Agent (full)",
      authorityAccent: "melon",
      useCases:
        "Infrastructure automation, closed-loop optimisation, edge triggers",
      maturity: "PILOT",
      maturityAccent: "blue",
    },
  ],

  /* ----- Adoption by BU (legacy lines 157-166) ----- */
  adoptionByBu: [
    { label: "Operations", pct: 78, accent: "green" },
    { label: "Customer Experience", pct: 65, accent: "true-blue" },
    { label: "Finance & Compliance", pct: 54, accent: "gold" },
    { label: "Product & Engineering", pct: 82, accent: "sky" },
    { label: "Human Resources", pct: 41, accent: "melon" },
    { label: "Risk & Governance", pct: 36, accent: "gold" },
    { label: "Strategy & Analytics", pct: 69, accent: "true-blue" },
    { label: "Legal & Contracts", pct: 28, accent: "green" },
  ],

  /* ----- Operating Mode Specifications (legacy lines 177-180) ----- */
  operatingModes: [
    {
      mode: "In-The-Loop",
      modeAccent: "gold",
      trigger:
        "Any action with irreversible downstream impact; confidence < 85%",
      latency: "< 4 hrs human review SLA",
      touchPoints: "Pre-action approval · Post-action sign-off",
      fallback: "Action blocked; escalated to Tier 2 human reviewer",
      risk: "LOW",
      riskAccent: "green",
    },
    {
      mode: "Above-The-Loop",
      modeAccent: "true-blue",
      trigger:
        "Confidence ≥ 85%; within pre-approved workflow boundary",
      latency: "< 30 min exception SLA",
      touchPoints: "Daily digest review · Exception alerts only",
      fallback: "Pause workflow; notify human monitor within 5 min",
      risk: "MEDIUM",
      riskAccent: "cyan",
    },
    {
      mode: "On-The-Loop",
      modeAccent: "sky",
      trigger:
        "Confidence ≥ 92%; action type on approved list; full audit trail active",
      latency: "< 2 hr review cycle",
      touchPoints:
        "Asynchronous audit · Weekly human-in-review session",
      fallback:
        "Auto-rollback if anomaly score > threshold; page on-call",
      risk: "MEDIUM-HIGH",
      riskAccent: "orange",
    },
    {
      mode: "Out-Of-Loop",
      modeAccent: "melon",
      trigger:
        "Pre-certified action class; closed system boundary; post-hoc review agreed",
      latency: "No real-time SLA",
      touchPoints: "Design-time sign-off · Post-hoc monthly review",
      fallback:
        "Circuit-breaker triggers; system self-halts; incident created",
      risk: "HIGH",
      riskAccent: "purple",
    },
  ],

  /* ----- Mode Transition Rules (legacy lines 187-190) ----- */
  modeTransitions: [
    {
      from: "IN-THE-LOOP",
      fromAccent: "orange",
      to: "ABOVE-THE-LOOP",
      toAccent: "blue",
      condition:
        "≥ 30 consecutive successful actions; confidence trend ≥ 88%",
      approver: "Process Owner + AI Lead",
      cooling: "2-week shadow period",
    },
    {
      from: "ABOVE-THE-LOOP",
      fromAccent: "blue",
      to: "ON-THE-LOOP",
      toAccent: "cyan",
      condition:
        "≥ 60-day stable operation; zero critical incidents; audit passed",
      approver: "Business Lead + Risk Officer",
      cooling: "4-week shadow period",
    },
    {
      from: "ON-THE-LOOP",
      fromAccent: "cyan",
      to: "OUT-OF-LOOP",
      toAccent: "purple",
      condition:
        "Board-level AI governance sign-off; external audit complete",
      approver: "C-Suite + External Auditor",
      cooling: "Formal certification programme",
    },
    {
      from: "Any",
      to: "IN-THE-LOOP",
      toAccent: "orange",
      condition:
        "Any critical incident, anomaly spike, or compliance flag",
      approver: "On-call Human Agent (auto)",
      cooling: "Immediate; no cooling period",
    },
  ],

  /* ----- Role Definitions (legacy lines 201-206) ----- */
  roleDefinitions: [
    {
      role: "AI Workflow Operator",
      responsibilities:
        "Day-to-day monitoring; approving in-loop actions; exception handling",
      interaction:
        "Direct task approval; real-time chat interface; override capability",
      authority: "Tactical (within policy)",
      skills:
        "Process knowledge · AI tool literacy · Critical thinking",
    },
    {
      role: "Human-in-Review",
      responsibilities:
        "Quality assurance of agent outputs; periodic audit; feedback loop maintenance",
      interaction:
        "Async review queue; sampling-based audit; scoring & annotation",
      authority: "Output acceptance / rejection",
      skills:
        "Domain expertise · Evaluation frameworks · Data literacy",
    },
    {
      role: "Process Owner",
      responsibilities:
        "Defines workflow boundaries; sets guardrails; approves mode transitions",
      interaction:
        "Policy configuration; threshold setting; escalation path owner",
      authority: "Strategic (workflow design)",
      skills:
        "Business process design · Risk awareness · Stakeholder management",
    },
    {
      role: "AI Lead / Prompt Engineer",
      responsibilities:
        "Maintains agent instructions; optimises prompts; monitors drift",
      interaction:
        "System prompt management; capability tuning; model evaluation",
      authority: "Technical (agent configuration)",
      skills:
        "Prompt engineering · LLM behaviour · Evaluation design",
    },
    {
      role: "On-Call Responder",
      responsibilities:
        "Receives escalation alerts; investigates incidents; triggers fallback",
      interaction:
        "Incident dashboard; manual override console; rollback controls",
      authority: "Emergency (system authority)",
      skills:
        "Incident response · System architecture · Calm under pressure",
    },
    {
      role: "Governance Officer",
      responsibilities:
        "Ensures regulatory compliance; maintains AI registry; approves high-risk transitions",
      interaction:
        "Audit log access; compliance reporting; policy enforcement",
      authority: "Compliance (regulatory gate)",
      skills:
        "Regulatory knowledge · Risk frameworks · Documentation",
    },
  ],

  /* ----- Role Allocation by Mode (legacy lines 213-216) ----- */
  roleAllocations: [
    {
      role: "AI Workflow Operator",
      in: "● Full-time",
      inAccent: "green",
      above: "◐ Part-time",
      aboveAccent: "gold",
      on: "○ On-call",
      onAccent: "muted",
      out: "— None",
      outAccent: "muted",
    },
    {
      role: "Human-in-Review",
      in: "◐ Sampling",
      inAccent: "gold",
      above: "● Full-time",
      aboveAccent: "green",
      on: "◐ Async",
      onAccent: "gold",
      out: "○ Post-hoc",
      outAccent: "muted",
    },
    {
      role: "Process Owner",
      in: "◐ Design + Escalation",
      inAccent: "gold",
      above: "◐ Exceptions",
      aboveAccent: "gold",
      on: "○ Monthly",
      onAccent: "muted",
      out: "○ Quarterly",
      outAccent: "muted",
    },
    {
      role: "On-Call Responder",
      in: "— Rarely needed",
      inAccent: "muted",
      above: "○ On-call",
      aboveAccent: "muted",
      on: "◐ Active",
      onAccent: "gold",
      out: "● Critical",
      outAccent: "green",
    },
  ],

  /* ----- Handoff Triggers (legacy lines 227-233) ----- */
  handoffTriggers: [
    {
      category: "Confidence Threshold",
      condition:
        "Agent confidence score falls below mode-specific minimum",
      severity: "HIGH",
      severityAccent: "orange",
      channel: "Dashboard alert + email",
      sla: "15 min",
      auto: "Pause task; await instruction",
    },
    {
      category: "Novel Scenario",
      condition:
        "Input pattern not seen in training or prior operation (> 3σ deviation)",
      severity: "HIGH",
      severityAccent: "orange",
      channel: "Dashboard alert + Slack",
      sla: "30 min",
      auto: "Log scenario; request human guidance",
    },
    {
      category: "Ethical Flag",
      condition:
        "Action classified as potentially harmful, biased, or legally sensitive",
      severity: "CRITICAL",
      severityAccent: "purple",
      channel: "Slack urgent + pager",
      sla: "Immediate",
      auto: "Halt; create incident; quarantine output",
    },
    {
      category: "Resource Limit",
      condition: "Cost, time, or compute budget threshold reached",
      severity: "MEDIUM",
      severityAccent: "cyan",
      channel: "Dashboard + email digest",
      sla: "1 hr",
      auto: "Throttle; request budget approval",
    },
    {
      category: "Contradictory Instructions",
      condition:
        "Policy conflict detected between system prompt and task instruction",
      severity: "HIGH",
      severityAccent: "orange",
      channel: "Dashboard alert",
      sla: "30 min",
      auto: "Pause; surface both instructions to human",
    },
    {
      category: "External Dependency Fail",
      condition:
        "Required tool, API, or data source unavailable or returning errors",
      severity: "MEDIUM",
      severityAccent: "cyan",
      channel: "Dashboard + email",
      sla: "1 hr",
      auto: "Retry ×3; failover if available; else escalate",
    },
    {
      category: "Scheduled Review",
      condition:
        "Periodic checkpoint reached (time-based, count-based, or milestone-based)",
      severity: "LOW",
      severityAccent: "blue",
      channel: "Digest email",
      sla: "24 hrs",
      auto: "Generate summary report; queue for review",
    },
  ],

  /* ----- Handoff Standards (legacy lines 240-243) ----- */
  handoffStandards: [
    {
      element: "Task Brief",
      required: "Objective · Success criteria · Constraints · Deadline",
      format: "Structured prompt template",
      validation: "Completeness check; ambiguity score < 0.2",
    },
    {
      element: "Context Package",
      required: "Relevant history · Prior outputs · Background documents",
      format: "JSON context bundle",
      validation: "Token count within model limits; file types validated",
    },
    {
      element: "Authority Scope",
      required:
        "Permitted actions · Prohibited actions · Escalation threshold",
      format: "Policy config block",
      validation: "No conflicting rules; all action types enumerated",
    },
    {
      element: "Review Cadence",
      required: "Check-in frequency · Output format · Delivery channel",
      format: "Schedule config",
      validation:
        "At least one review point per 4-hr operating window",
    },
  ],

  /* ----- Performance KPIs (legacy lines 251-254) ----- */
  performanceKpis: [
    {
      label: "Avg Task Completion Rate",
      value: "91%",
      sub: "Across all operating modes",
      accent: "green",
    },
    {
      label: "Mean Handoff Latency",
      value: "8.4m",
      sub: "Agent-to-human avg response",
      accent: "sky",
    },
    {
      label: "Critical Incidents (90d)",
      value: "3",
      sub: "All resolved within SLA",
      accent: "gold",
    },
    {
      label: "Override Rate",
      value: "4.1%",
      sub: "Human overrides of agent decisions",
      accent: "melon",
    },
  ],

  /* ----- Performance Benchmarks (legacy lines 260-266) ----- */
  performanceBenchmarks: [
    {
      metric: "Task Completion Rate",
      inLoop: "88%",
      inAccent: "green",
      aboveLoop: "93%",
      aboveAccent: "green",
      onLoop: "96%",
      onAccent: "green",
      outLoop: "98%",
      outAccent: "green",
      target: "≥ 85% per mode",
    },
    {
      metric: "Decision Accuracy",
      inLoop: "97%",
      inAccent: "green",
      aboveLoop: "94%",
      aboveAccent: "green",
      onLoop: "91%",
      onAccent: "gold",
      outLoop: "89%",
      outAccent: "gold",
      target: "≥ 92% for high-risk",
    },
    {
      metric: "Avg Cycle Time",
      inLoop: "4.2 hrs",
      aboveLoop: "1.8 hrs",
      onLoop: "0.6 hrs",
      outLoop: "0.1 hrs",
      target: "Varies by SLA",
    },
    {
      metric: "Human Effort (FTE hrs / task)",
      inLoop: "0.8 hrs",
      aboveLoop: "0.2 hrs",
      onLoop: "0.05 hrs",
      outLoop: "< 0.01 hrs",
      target: "Reduction target: 60%",
    },
    {
      metric: "Escalation Rate",
      inLoop: "22%",
      inAccent: "gold",
      aboveLoop: "8%",
      aboveAccent: "green",
      onLoop: "5%",
      onAccent: "green",
      outLoop: "2%",
      outAccent: "green",
      target: "< 15% across portfolio",
    },
    {
      metric: "Override Rate",
      inLoop: "6.2%",
      inAccent: "green",
      aboveLoop: "3.8%",
      aboveAccent: "green",
      onLoop: "2.1%",
      onAccent: "green",
      outLoop: "0.4%",
      outAccent: "green",
      target: "< 10%",
    },
    {
      metric: "Audit Trail Completeness",
      inLoop: "100%",
      inAccent: "green",
      aboveLoop: "100%",
      aboveAccent: "green",
      onLoop: "100%",
      onAccent: "green",
      outLoop: "100%",
      outAccent: "green",
      target: "100% mandatory",
    },
  ],

  /* ----- Quality Signal Thresholds (legacy lines 273-277) ----- */
  signalThresholds: [
    {
      signal: "Confidence Score",
      green: "≥ 85%",
      amber: "70–84%",
      red: "< 70%",
      frequency: "Per action",
    },
    {
      signal: "Anomaly Score",
      green: "< 1.5σ",
      amber: "1.5–2.5σ",
      red: "> 2.5σ",
      frequency: "Rolling 1-hour window",
    },
    {
      signal: "Latency vs SLA",
      green: "≤ 80% of SLA",
      amber: "80–100% of SLA",
      red: "Breached SLA",
      frequency: "Every 5 min",
    },
    {
      signal: "Human Override Rate (7d)",
      green: "< 5%",
      amber: "5–10%",
      red: "> 10%",
      frequency: "Daily rollup",
    },
    {
      signal: "Incident Frequency",
      green: "0 criticals (30d)",
      amber: "1–2 criticals (30d)",
      red: "3+ criticals (30d)",
      frequency: "Real-time",
    },
  ],
};

/**
 * Workshop id → data-table bundle. Keyed loosely so future workshops
 * can register overrides without churning the consumer signature.
 */
export const dataTablesByWorkshop: Record<string, WorkshopDataTables> = {
  WS4: WS4_DATA,
  ws4: WS4_DATA,
};

export function getDataTables(workshopId: string): WorkshopDataTables | null {
  return dataTablesByWorkshop[workshopId] ?? null;
}
